import React, { memo } from 'react';
import { formats, validators } from 'investira.sdk';
import dayjs from 'dayjs';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton, Icon, Tooltip } from 'investira.react.components';
import { ATIVO_MERCADO } from '@investirapri/financa.js/lib/enums';
import CorrelacaoCenarioTooltip from './CorrelacaoCenarioTooltip';
const CenariosDetalhes = memo(props => {
  const {
    data,
    onSetAtivos,
    onSetInfos,
    onSetCorrelacoes
  } = props;
  const {
    response
  } = data;
  let xCenarios = response?.detalhe?.cenarios;
  if (xCenarios && typeof xCenarios === 'object') {
    // Cria um array a partir das chaves do objeto, cada cenário é {key, ...cenario}
    xCenarios = Object.entries(xCenarios).map(([xKey, xValue]) => ({
      key: xKey,
      ...xValue
    }))
    // Ordena: "simulacao_dinamica" primeiro, depois por atributo 'inicio'
    .sort((xA, xB) => {
      if (xA.key === 'simulacao_dinamica') return -1;
      if (xB.key === 'simulacao_dinamica') return 1;
      // Se houver atributo inicio, ordenar pelo valor; senão, manter ordem
      if (xA.inicio && xB.inicio) {
        if (xA.inicio < xB.inicio) return -1;
        if (xA.inicio > xB.inicio) return 1;
      }
      return 0;
    });
  }
  const xItens = response?.detalhe?.itens;
  const xBenchmarks = response?.detalhe?.benchmarks;
  const xCorrelacoes = response?.detalhe?.correlacoes;
  const xFinanceiro = response?.resumo?.financeiro;
  const xImpactoCenarios = response?.resumo?.impacto?.cenarios;

  /**
   * Filtra ativos por cenário e retorna dados estruturados
   * @param {Object} pDados - Objeto com todos os ativos
   * @param {string} pCenario - Nome do cenário (ex: 'simulacao_dinamica', '202002_CORONA_VIRUS')
   * @returns {Array} Array de ativos filtrados com impacto no cenário
   */
  function filtrarAtivosPorCenario(pDados, pCenario) {
    const xResultado = [];

    // Itera sobre cada ativo no objeto de dados
    for (const [xKey, xAtivo] of Object.entries(pDados)) {
      // Verifica se o cenário existe e tem impacto financeiro
      if (xAtivo.cenarios && xAtivo.cenarios[pCenario]) {
        const xCenarioData = xAtivo.cenarios[pCenario];
        xResultado.push({
          ifeed_ativo_id: xAtivo.ativo_id,
          ativo_key: xKey,
          ativo: xAtivo.ativo,
          ativo_seuid: xAtivo.ativo_seuid,
          isin: xAtivo.isin || null,
          emissor: xAtivo.emissor || null,
          emissor_pessoa_id: xAtivo.emissor_pessoa_id || null,
          setor: xAtivo.emissor?.setor || null,
          mercado: xAtivo.mercado || null,
          ativo_tipo_id: xAtivo.ativo_tipo_id || null,
          financeiro: xAtivo.financeiro,
          impacto_fator: xCenarioData.impacto_fator,
          impacto_percentual: xCenarioData.impacto_fator * 100,
          impacto_financeiro: xCenarioData.impacto_financeiro,
          desvio: xAtivo.desvio || null,
          codigo: xAtivo.codigo || null,
          correlacao: xAtivo.correlacao || null,
          beta: xAtivo.correlacao?.beta || null,
          indicador_id: xAtivo.correlacao?.indicador_id || null,
          benchmark_id: xAtivo.correlacao?.id || null,
          prazo: xAtivo.prazo || null,
          marcacao: xAtivo.marcacao || null,
          vencimento: xAtivo.vencimento || null,
          curva_id: xAtivo.curva_id || null
        });
      }
    }

    // Ordena por impacto financeiro (decrescente)
    xResultado.sort((a, b) => b.impacto_financeiro - a.impacto_financeiro);
    return xResultado;
  }

  /**
   * Consolida os ativos filtrados por cenário agrupando por mercado.
   * Para cada mercado, soma o impacto_financeiro, soma impacto_percentual,
   * conta ativos e identifica o indicador_id mais frequente do grupo.
   * @param {Object} pDados - Objeto de itens (ativos) completos
   * @param {string} pCenario - Chave identificadora do cenário
   * @returns {Array} Array de objetos consolidados por mercado
   */
  function consolidarAtivosPorMercado(pDados, pBenchmark, pCenario) {
    if (validators.isEmpty(pDados) || validators.isNull(pCenario.cenario_id)) {
      return [];
    }

    // Primeiro, filtra os ativos por cenário
    const xAtivosPorCenario = filtrarAtivosPorCenario(pDados, pCenario.cenario_id);

    // Agrupa por mercado
    const xConsolidado = {};

    // Variações dos benchmarks no cenário corrente
    const xVariacoes = pCenario.variacoes;
    xAtivosPorCenario.forEach(xAtivo => {
      const xMercado = xAtivo.mercado ? ATIVO_MERCADO[xAtivo.mercado].descricao : 'Desconhecido';
      if (!xConsolidado[xMercado]) {
        xConsolidado[xMercado] = {
          mercado: xMercado,
          total_ativos: 0,
          impacto_financeiro_total: 0,
          impacto_percentual_total: 0,
          indicadores: {},
          variacoes: {},
          ativos: [] // novo atributo para armazenar os ativos
        };
      }
      xConsolidado[xMercado].total_ativos += 1;
      xConsolidado[xMercado].impacto_financeiro_total += xAtivo.impacto_financeiro;
      xConsolidado[xMercado].impacto_percentual_total += xAtivo.impacto_percentual;

      // Adiciona o ativo à lista de ativos desse mercado
      xConsolidado[xMercado].ativos.push(xAtivo);

      // Conta frequência de indicador_id
      const xBenchmarkId = xAtivo.benchmark_id || 'Desconhecido';
      xConsolidado[xMercado].indicadores[xBenchmarkId] = (xConsolidado[xMercado].indicadores[xBenchmarkId] || 0) + 1;
    });

    // Consolida resultado final, encontrando o indicador mais frequente de cada mercado
    const xConsolidadoArray = Object.values(xConsolidado).map(xItem => {
      // Encontrando o indicador_id mais frequente
      let xBenchmarkMaisFrequente = null;
      let xIndicadorCount = 0;
      Object.entries(xItem.indicadores).forEach(([pIndicadorId, pCount]) => {
        if (pCount > xIndicadorCount) {
          xBenchmarkMaisFrequente = pIndicadorId;
          xIndicadorCount = pCount;
        }
      });
      return {
        mercado: xItem.mercado,
        total_ativos: xItem.total_ativos,
        impacto_financeiro_total: xItem.impacto_financeiro_total,
        impacto_percentual_total: xItem.impacto_percentual_total,
        maior_correlacao: pBenchmark[xBenchmarkMaisFrequente] && pBenchmark[xBenchmarkMaisFrequente].indicador_id,
        variacoes: xVariacoes,
        ativos: xItem.ativos // inclui o novo atributo 'ativos'
      };
    });
    return xConsolidadoArray;
  }

  /**
   * Retorna impacto_financeiro de um cenário específico pelo cenario_id
   * @param {Array} pArrayCenarios - Array de objetos cenários
   * @param {string} pCenarioId - Id do cenário desejado
   * @returns {number|null} impacto_financeiro ou null se não encontrado
   *
   */
  function obterImpactoFinanceiroDoCenario(pCenarioId, pArrayCenarios = xImpactoCenarios) {
    if (!Array.isArray(pArrayCenarios) || validators.isNull(pCenarioId) || validators.isEmpty(pCenarioId)) {
      return null;
    }
    const xCenarioAchado = pArrayCenarios.find(xCenario => xCenario.cenario_id === pCenarioId);
    return xCenarioAchado ? xCenarioAchado.impacto_financeiro : null;
  }

  // Função auxiliar para obter estatísticas do cenário

  if (validators.isEmpty(xCenarios) || validators.isEmpty(xItens) || validators.isEmpty(xBenchmarks)) {
    return null;
  }
  return <TableContainer id="table-container" sx={{
    overflow: 'auto'
  }}>
      <Table size="small" sx={{
      borderColor: 'secondary'
    }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{
            maxWidth: '50px'
          }}>Ano</TableCell>
            <TableCell>Cenário</TableCell>
            <TableCell sx={{
            width: '100%'
          }}>Período</TableCell>
            {/* <TableCell>Maior Correlação</TableCell> */}
            <TableCell sx={{
            textAlign: 'right',
            whiteSpace: 'nowrap'
          }}>Impacto (%)</TableCell>
            <TableCell sx={{
            textAlign: 'right'
          }}>Impacto (R$)</TableCell>
            <TableCell sx={{
            maxWidth: '44px'
          }}></TableCell>
          </TableRow>
        </TableHead>
        {/* loop */}
        {Object.values(xCenarios).map((xCenario, xIndex) => {
        const xImpactoFinanceiro = obterImpactoFinanceiroDoCenario(xCenario.cenario_id);
        const xPeriodo = xCenario.inicio ? `${dayjs(xCenario.inicio).format('DD/MM/YYYY')} - ${xCenario.fim && dayjs(xCenario.fim).format('DD/MM/YYYY')}` : '';
        console.log('xCenario', xCenario);
        return <React.Fragment key={xIndex}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                maxWidth: '50px'
              }}>
                    {dayjs(xCenario.inicio).format('YYYY')}
                  </TableCell>
                  <TableCell sx={{
                whiteSpace: 'nowrap'
              }}>{xCenario.cenario}</TableCell>
                  <TableCell sx={{
                width: '100%'
              }}>{xPeriodo}</TableCell>
                  {/* <TableCell></TableCell> */}
                  <TableCell sx={{
                textAlign: 'right'
              }}></TableCell>
                  <TableCell sx={{
                textAlign: 'right'
              }}>
                    {xImpactoFinanceiro && formats.formatNumber(xImpactoFinanceiro, 2, true)}
                  </TableCell>
                  <TableCell sx={{
                maxWidth: '44px'
              }}>
                    {xCenario.variacoes && <Tooltip title={<CorrelacaoCenarioTooltip variacoes={xCenario.variacoes} />} arrow={true}>
                        <IconButton color="primary" size="small">
                          <Icon iconName="chart_lines" size={16} />
                        </IconButton>
                      </Tooltip>}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Agrupar por mercado */}
                {/* no detalhe listar os ativos por mercado e tipo de ativo */}
                {consolidarAtivosPorMercado(xItens, xBenchmarks, xCenario).map((xItem, xIndex) => {
              return <TableRow key={crypto.randomUUID()}>
                      <TableCell colSpan={3} sx={{
                  width: '100%'
                }}>
                        {xItem.mercado}
                      </TableCell>
                      <TableCell sx={{
                  textAlign: 'right'
                }}>
                        {formats.formatNumber(xItem.impacto_financeiro_total / xFinanceiro * 100, 2)}
                      </TableCell>
                      <TableCell sx={{
                  textAlign: 'right'
                }}>
                        {xItem.impacto_financeiro_total && formats.formatNumber(xItem.impacto_financeiro_total, 2, true, false)}
                      </TableCell>
                      <TableCell>
                        <IconButton color="primary" size="small" onClick={() => {
                    onSetAtivos(xItem.ativos);
                    onSetCorrelacoes(xCorrelacoes);
                    onSetInfos({
                      mercado: xItem.mercado,
                      cenario: xCenario,
                      maior_correlacao: xItem.maior_correlacao,
                      impacto_percentual_total: xItem.impacto_financeiro_total / xFinanceiro * 100,
                      impacto_financeiro_total: xItem.impacto_financeiro_total,
                      variacoes: xItem.variacoes,
                      periodo: xPeriodo
                    });
                  }}>
                          <Icon iconName="eye" size={21} />
                        </IconButton>
                      </TableCell>
                    </TableRow>;
            })}
              </TableBody>
            </React.Fragment>;
      })}
      </Table>
    </TableContainer>;
});
export default CenariosDetalhes;
